import React, { useEffect, useState } from "react";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import FileUpload from "../../../../components/FileUpload/FileUpload";
import useToastAlert from "@/hooks/useToastAlert";
import useApi from "@/hooks/useApi";
import { Severity } from "@velaro/velaro-component-lib";
import { UploadedFile } from "../../FileStorage/types";
import LoadingSpinner from "@/components/LoadingSpinner";
import Panel from "@/components/Panel";
import UploadedFileEditor from "../../FileStorage/UploadedFileEditor";
import FilesEditor from "@/components/FileUpload/FilesEditor";

const JSON_FORMAT_EXAMPLE = `[
  { "question": "What is your return policy?", "answer": "30-day returns on all items." },
  { "title": "Shipping", "content": "Free shipping on orders over $50." }
]`;

function JsonFormatGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 rounded-md border border-blue-100 bg-blue-50 text-xs text-slate-700">
      <button
        className="flex w-full items-center justify-between px-3 py-2 font-medium text-blue-700"
        onClick={() => setOpen((v) => !v)}
      >
        <span>Best format for JSON files</span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="border-t border-blue-100 px-3 pb-3 pt-2 space-y-2">
          <p>
            JSON files are automatically converted to Q&amp;A pairs or titled
            sections — <strong>no vector indexes required</strong>. Use one of
            these shapes for best results:
          </p>
          <pre className="overflow-x-auto rounded bg-white p-2 text-[11px] leading-relaxed border border-blue-100">
            {JSON_FORMAT_EXAMPLE}
          </pre>
          <ul className="list-disc pl-4 space-y-1">
            <li>
              <strong>Q&amp;A:</strong>{" "}
              <code className="bg-white px-1 rounded">
                {"[{ question, answer }, ...]"}
              </code>
            </li>
            <li>
              <strong>Sections:</strong>{" "}
              <code className="bg-white px-1 rounded">
                {"[{ title, content }, ...]"}
              </code>{" "}
              (also accepts <code className="bg-white px-1 rounded">body</code>{" "}
              or <code className="bg-white px-1 rounded">description</code>)
            </li>
            <li>
              <strong>Plain TXT/MD:</strong> use{" "}
              <code className="bg-white px-1 rounded">## Heading</code> to split
              into sections — each becomes its own searchable chunk.
            </li>
          </ul>
          <p className="text-slate-500">
            Raw exports (CSV dumps, unstructured JSON arrays) produce poor
            search results. Structure your content as Q&amp;A or headings first.
          </p>
        </div>
      )}
    </div>
  );
}

interface Props {
  allowDrop?: boolean;
}

export default function AIChatbotFileTrainingIndex(props: Props) {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToastAlert();
  const api = useApi();

  async function save() {
    setLoading(true);
    const formData = new FormData();

    for (const file of newFiles) {
      formData.append("files", file);
    }
    formData.append("type", "AI");

    const result = await api.messaging.postFormData("uploadedFiles", formData);
    if (result.status !== 200) {
      setLoading(false);
      toast.displayToast(
        Severity.Error,
        "Error uploading files: " + (await result.text())
      );
      return;
    }

    setFiles([...files, ...(await result.json())]);
    setNewFiles([]);
    toast.displayToast(Severity.Success, "Files uploaded successfully");
    setLoading(false);
  }

  async function remove(file: UploadedFile) {
    await api.messaging.delete("uploadedFiles", { id: file.id });
    const clone = [...files];
    clone.splice(files.indexOf(file), 1);
    setFiles(clone);
  }

  useEffect(() => {
    async function fetchFiles() {
      const response = await api.messaging.get(`uploadedFiles/list/${"AI"}`);
      setFiles(await response.json());
      setLoading(false);
    }
    fetchFiles();
  }, [api.messaging]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {props.allowDrop && (
        <div className="mt-4">
          <FileUpload onAddFiles={setNewFiles} multiple>
            <div className="flex flex-row text-slate-500 text-xs">
              Supported files: TXT, MD, JSON, DOCX. Images won&apos;t be
              indexed.
            </div>
            <div className="flex flex-row text-slate-500 text-xs">
              You can upload a maximum of 5 files at once, with each file not
              exceeding 20 MB.
            </div>
          </FileUpload>
          <JsonFormatGuide />
          <FilesEditor files={newFiles} onUpdate={setNewFiles} />
        </div>
      )}
      <div>
        {props.allowDrop && (
          <div className="flex justify-end mt-4">
            <PrimaryBlueButton
              label="Upload"
              disabled={newFiles.length === 0}
              onClick={() => save()}
            />
          </div>
        )}
        {files.length > 0 && (
          <div className="mt-4">
            <Panel title={"Uploaded"}>
              <div className="p-4">
                <UploadedFileEditor files={files} onRemove={remove} />
              </div>
            </Panel>
          </div>
        )}
      </div>
    </>
  );
}
